import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import { queryKeys } from "./keys";
import type {
  CreateWaterSourceInput,
  ListResponse,
  UpdateWaterSourceInput,
  WaterSource,
} from "./types";

const base = "/v1/water-sources";

export function useWaterSources() {
  return useQuery({
    queryKey: queryKeys.waterSources.list(),
    queryFn: ({ signal }) =>
      api.get<ListResponse<WaterSource>>(base, undefined, signal),
  });
}

export function useWaterSource(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? queryKeys.waterSources.detail(id)
      : queryKeys.waterSources.all,
    queryFn: ({ signal }) =>
      api.get<WaterSource>(`${base}/${id}`, undefined, signal),
    enabled: !!id,
  });
}

export function useCreateWaterSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWaterSourceInput) =>
      api.post<WaterSource>(base, input),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: queryKeys.waterSources.all });
      qc.setQueryData(queryKeys.waterSources.detail(created.id), created);
    },
  });
}

export function useUpdateWaterSource(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWaterSourceInput) =>
      api.patch<WaterSource>(`${base}/${id}`, input),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.waterSources.detail(id), updated);
      qc.invalidateQueries({ queryKey: queryKeys.waterSources.all });
    },
  });
}

export function useArchiveWaterSource(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string | undefined) =>
      api.delete<void>(`${base}/${id}`, { reason: reason ?? "" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.waterSources.all });
    },
  });
}
